import { ChangeEvent, Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CircleAlertIcon, CircleHelpIcon, InfoIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { useForm, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import Link from '@/components/atoms/Link/Link';
import OptionRadioButton from '@/components/atoms/OptionRadioButton/OptionRadioButton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import useDialogAlert from '@/hooks/useDialogAlert';
import { cn } from '@/lib/utils';
import { selectNodeIdByActionId, selectSelectedAction } from '@/redux/dialogs/selectors';
import { updateAction } from '@/redux/nodes/actions';
import { Action } from '@/shared/types/action';
import { RootState } from '@/shared/types/state';
import { createYupResolver } from '@/util/forms/createYupResolver';
import { formatErrorMessage, getFormErrorsKey } from '@/util/util';
import {
  HANDOVER_OPTIONS,
  MAX_SIP_HEADERS,
  VOICE_HANDOVER_TRANSPORT,
} from '@/util/validation/constants';
import { sipUriPattern } from '@/util/validation/patterns';
import { handOverActionSchema } from '@/util/validation/schemas/action-schemas';

import ToolkitWrapper from '../../ToolkitWrapper';

type SipHeader = { id: string; key: string; value: string };
type Form = {
  handover: string;
  department_id: string;
  voice_handover_destination: string;
  voice_handover_transport: string;
};

function ToolkitActionHandover() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { action_id, external, department_id, metadata, type } = useSelector(
    (state: RootState) => selectSelectedAction(state) as Extract<Action, { type: 'handover' }>,
    shallowEqual
  );
  const parentNodeId = useSelector(selectNodeIdByActionId(action_id));

  // Default values for the form
  const defaultHandover = useMemo(() => {
    if (external) {
      return HANDOVER_OPTIONS.FACEBOOK_MESSENGER_HANDOVER;
    } else if (typeof department_id === 'string') {
      return HANDOVER_OPTIONS.ZENDESK_DEPARTMENT_ROUTING;
    } else if (metadata?.voice_handover_destination) {
      return HANDOVER_OPTIONS.VOICE_HANDOVER;
    } else {
      return HANDOVER_OPTIONS.DEFAULT;
    }
  }, [department_id, external, metadata?.voice_handover_destination]);
  const defaultDepartmentId = department_id ? department_id : '';
  const defaultVoiceHandoverDestination = metadata?.voice_handover_destination || '';
  const defaultVoiceHandoverTransport =
    metadata?.voice_handover_transport || VOICE_HANDOVER_TRANSPORT.DIAL;

  const defaultAttributes: SipHeader[] = useMemo(() => {
    const attrs = metadata?.voice_handover_attributes;
    if (!attrs || Object.keys(attrs).length === 0) {
      return [];
    }
    return (Object.entries(attrs) as [string, string][]).map(([key, value]) => ({
      id: crypto.randomUUID(),
      key,
      value,
    }));
  }, [metadata?.voice_handover_attributes]);

  const actionHandoverOptions = [
    { id: 0, value: HANDOVER_OPTIONS.DEFAULT, label: t('actions.handover.default.value') },
    {
      id: 1,
      value: HANDOVER_OPTIONS.ZENDESK_DEPARTMENT_ROUTING,
      label: t('actions.handover.zendesk_department_routing.value'),
    },
    {
      id: 2,
      value: HANDOVER_OPTIONS.FACEBOOK_MESSENGER_HANDOVER,
      label: t('actions.handover.facebook_messenger_handover.value'),
    },
    {
      id: 3,
      value: HANDOVER_OPTIONS.VOICE_HANDOVER,
      label: t('actions.handover.voice_handover.value'),
    },
  ];

  const form = useForm<Form>({
    mode: 'onChange',
    defaultValues: {
      handover: defaultHandover,
      department_id: defaultDepartmentId,
      voice_handover_destination: defaultVoiceHandoverDestination,
      voice_handover_transport: defaultVoiceHandoverTransport,
    },
    resolver: createYupResolver<Form>(handOverActionSchema),
  });
  const {
    control,
    trigger,
    setValue,
    formState: { errors },
  } = form;

  const watchHandover = useWatch({ control, name: 'handover' });
  const watchTransport = useWatch({ control, name: 'voice_handover_transport' });

  const transportOptions = [
    {
      id: VOICE_HANDOVER_TRANSPORT.DIAL,
      value: VOICE_HANDOVER_TRANSPORT.DIAL,
      label: (
        <span className="flex flex-col">
          <span>{t('actions.handover.voice_handover.transport_dial')}</span>
          <span className="text-xs text-muted-foreground">
            {t('actions.handover.voice_handover.transport_dial_help')}
          </span>
        </span>
      ),
    },
    {
      id: VOICE_HANDOVER_TRANSPORT.REFER,
      value: VOICE_HANDOVER_TRANSPORT.REFER,
      label: (
        <span className="flex flex-col">
          <span>{t('actions.handover.voice_handover.transport_refer')}</span>
          <span className="text-xs text-muted-foreground">
            {t('actions.handover.voice_handover.transport_refer_help')}
          </span>
        </span>
      ),
    },
  ];

  const [sipHeaders, setSipHeaders] = useState<SipHeader[]>(defaultAttributes);
  const lastHeaderRef = useRef<HTMLInputElement>(null);

  const duplicateHeaderNames = useMemo(() => {
    const headerNames = new Set<string>();
    const duplicates = new Set<string>();
    for (const header of sipHeaders) {
      const trimmed = header.key.trim();
      if (trimmed && headerNames.has(trimmed)) {
        duplicates.add(trimmed);
      }
      headerNames.add(trimmed);
    }
    return duplicates;
  }, [sipHeaders]);

  const handleChangeHandover = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setValue('handover', e.target.value, { shouldValidate: true });
      const external = e.target.value === HANDOVER_OPTIONS.FACEBOOK_MESSENGER_HANDOVER;
      let department_id = undefined;
      let metadata = undefined;

      if (e.target.value === HANDOVER_OPTIONS.ZENDESK_DEPARTMENT_ROUTING) {
        department_id = t('dialog.pre-fill.department_id');
        setValue('department_id', department_id, { shouldValidate: true });
      }

      if (e.target.value === HANDOVER_OPTIONS.VOICE_HANDOVER) {
        const voiceHandoverDestination = '+12025551234';
        metadata = {
          voice_handover_destination: voiceHandoverDestination,
          voice_handover_attributes: undefined,
        };
        setValue('voice_handover_destination', voiceHandoverDestination, { shouldValidate: true });
        // Default to dial; the prefilled destination is a phone number, where refer is invalid.
        setValue('voice_handover_transport', VOICE_HANDOVER_TRANSPORT.DIAL, {
          shouldValidate: true,
        });
        setSipHeaders([]);
      }

      dispatch(
        updateAction({ actionId: action_id, action: { external, department_id, metadata } })
      );
    },
    [action_id, dispatch, setValue, t]
  );

  const handleChangeDepartmentId = useCallback(
    (departmentId: string) => {
      dispatch(updateAction({ actionId: action_id, action: { department_id: departmentId } }));
    },
    [action_id, dispatch]
  );

  const handleChangeVoiceHandoverDestination = useCallback(
    (destination: string) => {
      // Refer needs a SIP destination; if the edit makes it non-SIP, fall back to
      // dial so the user isn't parked on an error (mirrors the prefill default).
      const resetToDial =
        metadata?.voice_handover_transport === VOICE_HANDOVER_TRANSPORT.REFER &&
        !sipUriPattern.test(destination);
      const transport = resetToDial
        ? VOICE_HANDOVER_TRANSPORT.DIAL
        : metadata?.voice_handover_transport;
      if (resetToDial) {
        setValue('voice_handover_transport', VOICE_HANDOVER_TRANSPORT.DIAL, {
          shouldValidate: true,
        });
      }
      dispatch(
        updateAction({
          actionId: action_id,
          action: {
            metadata: {
              voice_handover_destination: destination,
              voice_handover_attributes: metadata?.voice_handover_attributes,
              voice_handover_transport: transport,
            },
          },
        })
      );
      // transport validity depends on the destination (refer needs SIP), so re-check it.
      void trigger('voice_handover_transport');
    },
    [
      action_id,
      dispatch,
      setValue,
      metadata?.voice_handover_attributes,
      metadata?.voice_handover_transport,
      trigger,
    ]
  );

  const handleChangeVoiceHandoverTransport = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const transport = e.target.value;
      setValue('voice_handover_transport', transport, { shouldValidate: true });
      dispatch(
        updateAction({
          actionId: action_id,
          action: {
            metadata: {
              voice_handover_destination: metadata?.voice_handover_destination ?? '',
              voice_handover_attributes: metadata?.voice_handover_attributes,
              voice_handover_transport: transport,
            },
          },
        })
      );
    },
    [
      action_id,
      dispatch,
      setValue,
      metadata?.voice_handover_destination,
      metadata?.voice_handover_attributes,
    ]
  );

  const dispatchAttributes = useCallback(
    (headers: SipHeader[]) => {
      const attrs: Record<string, string> = {};
      for (const h of headers) {
        const trimmedKey = h.key.trim();
        if (trimmedKey && h.value.trim() && !(trimmedKey in attrs)) {
          attrs[trimmedKey] = h.value;
        }
      }
      dispatch(
        updateAction({
          actionId: action_id,
          action: {
            metadata: {
              voice_handover_destination: metadata?.voice_handover_destination ?? '',
              voice_handover_attributes: Object.keys(attrs).length > 0 ? attrs : undefined,
              voice_handover_transport: metadata?.voice_handover_transport,
            },
          },
        })
      );
    },
    [action_id, dispatch, metadata?.voice_handover_destination, metadata?.voice_handover_transport]
  );

  const handleAddHeader = useCallback(() => {
    setSipHeaders((prev) => [...prev, { id: crypto.randomUUID(), key: '', value: '' }]);
    requestAnimationFrame(() => lastHeaderRef.current?.focus());
  }, []);

  const handleRemoveHeader = useCallback(
    (index: number) => {
      setSipHeaders((prev) => {
        const updated = prev.filter((_, i) => i !== index);
        dispatchAttributes(updated);
        return updated;
      });
    },
    [dispatchAttributes]
  );

  const handleHeaderChange = useCallback(
    (index: number, field: 'key' | 'value', newValue: string) => {
      setSipHeaders((prev) => {
        const updated = prev.map((h, i) => (i === index ? { ...h, [field]: newValue } : h));
        dispatchAttributes(updated);
        return updated;
      });
    },
    [dispatchAttributes]
  );

  const { setFieldError } = useDialogAlert({
    id: action_id,
    nodeId: parentNodeId,
    title: t('actions.types.handover'),
    type,
  });

  useEffect(() => {
    trigger();
  }, [trigger]);

  useEffect(() => {
    const fields = [
      'department_id',
      'voice_handover_destination',
      'voice_handover_transport',
    ] as const;

    for (const field of fields) {
      setFieldError(field, errors[field]?.message);
    }
  }, [getFormErrorsKey(errors), setFieldError]);

  return (
    <ToolkitWrapper type="handover">
      <Form {...form}>
        {actionHandoverOptions.map((option, index) => (
          <div
            className="mb-6"
            key={`${actionHandoverOptions[index].id}-${actionHandoverOptions[index].value}`}
          >
            <OptionRadioButton
              option={option}
              size="medium"
              name="handover"
              onChange={handleChangeHandover}
              isSelected={watchHandover === option.value}
            />
            {watchHandover === HANDOVER_OPTIONS.ZENDESK_DEPARTMENT_ROUTING &&
              watchHandover === option.value && (
                <div className="mt-2">
                  <Alert variant="info">
                    <InfoIcon size={16} />
                    <AlertDescription>
                      {t('actions.handover.zendesk_department_routing.help_text')}
                    </AlertDescription>
                  </Alert>
                  <div className="mt-3.5">
                    <FormField
                      control={control}
                      name="department_id"
                      shouldUnregister
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex items-center gap-1">
                            {t('actions.handover.departement_routing')}
                            <InfoTooltip
                              content={t('actions.handover.departement_routing_tooltip')}
                              side="right"
                            >
                              <CircleHelpIcon className="size-3.5 text-muted-foreground" />
                            </InfoTooltip>
                          </FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              autoFocus
                              placeholder={t('actions.handover.departement_routing_placeholder')}
                              onChange={(event) => {
                                field.onChange(event);
                                handleChangeDepartmentId(event.target.value);
                              }}
                              onBlur={(event) => {
                                const trimmed = event.target.value.trim();
                                if (trimmed !== event.target.value) {
                                  field.onChange(trimmed);
                                  handleChangeDepartmentId(trimmed);
                                }
                                field.onBlur();
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              )}
            {watchHandover === HANDOVER_OPTIONS.VOICE_HANDOVER &&
              watchHandover === option.value && (
                <div className="mt-2">
                  <Alert variant="info">
                    <InfoIcon size={16} />
                    <AlertDescription>
                      <p>
                        <Trans
                          i18nKey="actions.handover.voice_handover.help_text"
                          components={{
                            link1: (
                              <Link
                                href="https://docs.moveo.ai/docs/ai-agents/operations/handover#external-handover"
                                external
                              />
                            ),
                          }}
                        />
                      </p>
                    </AlertDescription>
                  </Alert>
                  <div className="mt-3.5">
                    <FormField
                      control={control}
                      name="voice_handover_destination"
                      shouldUnregister
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('actions.handover.voice_handover.label')}</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              autoFocus
                              placeholder={t('actions.handover.voice_handover.placeholder')}
                              onChange={(event) => {
                                field.onChange(event);
                                handleChangeVoiceHandoverDestination(event.target.value);
                              }}
                              onBlur={(event) => {
                                const trimmed = event.target.value.trim();
                                if (trimmed !== event.target.value) {
                                  field.onChange(trimmed);
                                  handleChangeVoiceHandoverDestination(trimmed);
                                }
                                field.onBlur();
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Transport selector: Standard transfer (Dial) vs SIP REFER */}
                  <div className="mt-4">
                    <span className="text-sm leading-5 font-semibold text-foreground">
                      {t('actions.handover.voice_handover.transport_label')}
                    </span>
                    <div className="mt-2 flex flex-col gap-2">
                      {transportOptions.map((option) => (
                        <OptionRadioButton
                          key={option.id}
                          option={option}
                          size="medium"
                          name="voice_handover_transport"
                          onChange={handleChangeVoiceHandoverTransport}
                          isSelected={watchTransport === option.value}
                        />
                      ))}
                    </div>
                    {errors.voice_handover_transport && (
                      <span className="mt-1 block text-xs text-destructive">
                        {formatErrorMessage(errors.voice_handover_transport)}
                      </span>
                    )}
                    {watchTransport === VOICE_HANDOVER_TRANSPORT.REFER && (
                      <div className="mt-2">
                        <Alert variant="warning">
                          <CircleAlertIcon size={16} />
                          <AlertDescription>
                            {t('actions.handover.voice_handover.transport_sip_leg_warning')}
                          </AlertDescription>
                        </Alert>
                      </div>
                    )}
                  </div>

                  {/* SIP Headers key-value editor */}
                  <div className="mt-4">
                    <div className="flex items-center">
                      <span className="text-sm leading-5 font-semibold text-foreground">
                        {t('actions.handover.voice_handover.attributes_label')}
                      </span>
                      <InfoTooltip
                        content={t('actions.handover.voice_handover.attributes_help_text', {
                          0: '{{context.variable}}',
                        })}
                        side="top"
                      >
                        <InfoIcon size={14} className="ml-1 text-muted-foreground" />
                      </InfoTooltip>
                    </div>
                    <div className="mt-2 grid grid-cols-[1fr_1fr_auto] items-center gap-x-2 gap-y-2">
                      {sipHeaders.length > 0 && (
                        <div className="col-span-full grid grid-cols-subgrid">
                          <Label>{t('actions.handover.voice_handover.attributes_key_label')}</Label>
                          <Label>
                            {t('actions.handover.voice_handover.attributes_value_label')}
                          </Label>
                        </div>
                      )}
                      {sipHeaders.map((header, index) => {
                        const isDuplicate = duplicateHeaderNames.has(header.key.trim());
                        return (
                          <Fragment key={header.id}>
                            <Input
                              ref={index === sipHeaders.length - 1 ? lastHeaderRef : undefined}
                              placeholder={t(
                                'actions.handover.voice_handover.attributes_key_placeholder'
                              )}
                              value={header.key}
                              onChange={(e) => handleHeaderChange(index, 'key', e.target.value)}
                              className={cn(isDuplicate && 'border-destructive')}
                            />
                            <Input
                              placeholder={t(
                                'actions.handover.voice_handover.attributes_value_placeholder'
                              )}
                              value={header.value}
                              onChange={(e) => handleHeaderChange(index, 'value', e.target.value)}
                            />
                            <Button
                              type="button"
                              variant="ghost-neutral"
                              size="icon-md"
                              className="hover:[&_svg]:text-destructive"
                              aria-label={t('actions.handover.voice_handover.remove_attribute')}
                              onClick={() => handleRemoveHeader(index)}
                            >
                              <Trash2Icon size={16} />
                            </Button>
                            {isDuplicate && (
                              <span className="col-span-full text-xs text-destructive">
                                {t('actions.handover.voice_handover.duplicate_key')}
                              </span>
                            )}
                          </Fragment>
                        );
                      })}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleAddHeader}
                      className="mt-2"
                      disabled={sipHeaders.length >= MAX_SIP_HEADERS}
                    >
                      <PlusIcon className="mr-1 size-4 text-muted-foreground" />
                      {t('actions.handover.voice_handover.add_attribute')}
                    </Button>
                  </div>
                </div>
              )}
          </div>
        ))}
      </Form>
    </ToolkitWrapper>
  );
}

export default ToolkitActionHandover;
